import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Slider,
  Space,
  Spin,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  ExperimentOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  SaveOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from 'store';
import { createABTest, selectABTestLoading, updateABTest } from 'store/slices/abTest.slice';
import { fetchSegments, selectSegments } from 'store/slices/push.slice';
import { fetchApplications, selectApplications } from 'store/slices/applications.slice';
import { abTestApi } from 'api/endpoints/ab_test.api';
import {
  ABAudiencePreview,
  ABTestCreateRequest,
  ABTestVariantCreateRequest,
} from 'types/ab_test.types';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';

const { TextArea } = Input;
const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const LOCALES = ['ru', 'en', 'uk', 'kz', 'uz', 'az', 'tr', 'pt', 'es', 'de', 'sv', 'fr'];
const OS_OPTIONS = ['android', 'ios', 'web'];

interface VariantForm {
  key: string;
  name: string;
  is_control: boolean;
  traffic_percentage: number;
  title: string;
  body: string;
  image_url?: string;
  click_url?: string;
  custom_data?: string;
}

const newKey = () => `v_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const DEFAULT_VARIANTS: VariantForm[] = [
  { key: 'control', name: 'Control', is_control: true, traffic_percentage: 50, title: '', body: '' },
  { key: 'variant_a', name: 'Variant A', is_control: false, traffic_percentage: 50, title: '', body: '' },
];

const PushPreview: React.FC<{ title: string; body: string; imageUrl?: string }> = ({
  title,
  body,
  imageUrl,
}) => (
  <div
    style={{
      border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 12,
      padding: 12,
      background: 'rgba(255,255,255,0.04)',
    }}
  >
    <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
      сейчас
    </Text>
    <div style={{ fontWeight: 600, marginTop: 2, wordBreak: 'break-word' }}>
      {title || 'Заголовок'}
    </div>
    <div style={{ fontSize: 13, opacity: 0.8, wordBreak: 'break-word' }}>
      {body || 'Текст уведомления'}
    </div>
    {imageUrl && (
      <img
        src={imageUrl}
        alt=""
        style={{ marginTop: 8, maxWidth: '100%', maxHeight: 90, borderRadius: 8 }}
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = 'none';
        }}
      />
    )}
  </div>
);

const ABTestForm: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const editId = id ? Number(id) : undefined;
  const isEdit = Boolean(editId);

  const [form] = Form.useForm();
  const [variantForm] = Form.useForm();

  const applications = useAppSelector(selectApplications) || [];
  const segments = useAppSelector(selectSegments) || [];
  const isSaving = useAppSelector(selectABTestLoading);

  const [variants, setVariants] = useState<VariantForm[]>(DEFAULT_VARIANTS);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingVariant, setEditingVariant] = useState<VariantForm | null>(null);
  const [useSegment, setUseSegment] = useState(true);
  const [loadingTest, setLoadingTest] = useState(isEdit);

  const [audience, setAudience] = useState<ABAudiencePreview | null>(null);
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [audienceError, setAudienceError] = useState<string>();

  const selectedAppId = Form.useWatch('app_id', form);
  const segmentId = Form.useWatch('segment_id', form);
  const locale = Form.useWatch('locale', form);
  const os = Form.useWatch('os', form);
  const offer = Form.useWatch('offer', form);
  const hasReg = Form.useWatch('has_registration', form);
  const hasDep = Form.useWatch('has_deposit', form);

  useEffect(() => {
    dispatch(fetchApplications({ limit: 1000, offset: 0 }));
  }, [dispatch]);

  useEffect(() => {
    if (selectedAppId) {
      dispatch(fetchSegments({ appId: selectedAppId, limit: 1000, offset: 0 }));
    }
  }, [selectedAppId, dispatch]);

  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    (async () => {
      try {
        const test = await abTestApi.get(editId);
        if (cancelled) return;

        const segId = test.segment_id ?? undefined;
        setUseSegment(Boolean(segId));
        form.setFieldsValue({
          app_id: test.app_id,
          name: test.name,
          description: test.description,
          segment_id: segId,
          locale: test.locale || undefined,
          os: test.os || undefined,
          offer: test.offer || undefined,
          has_registration: test.has_registration,
          has_deposit: test.has_deposit,
          dateRange: [
            dayjs(test.start_date),
            test.end_date ? dayjs(test.end_date) : dayjs(test.start_date).add(7, 'day'),
          ],
        });
        setVariants(
          (test.variants || []).map((v) => ({
            key: String(v.id),
            name: v.name,
            is_control: v.is_control,
            traffic_percentage: v.traffic_percentage,
            title: v.title,
            body: v.body,
            image_url: v.image_url || undefined,
            click_url: v.click_url || undefined,
            custom_data: v.custom_data ? JSON.stringify(v.custom_data, null, 2) : undefined,
          }))
        );
      } catch (e: any) {
        notify.error(e?.response?.data?.error || 'Не удалось загрузить тест');
        navigate(ROUTES.PUSH_CENTER);
      } finally {
        if (!cancelled) setLoadingTest(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editId, form, navigate]);

  const totalPercentage = variants.reduce((s, v) => s + (v.traffic_percentage || 0), 0);
  const isPercentageValid = totalPercentage === 100;
  const hasControl = variants.some((v) => v.is_control);
  const allFilled = variants.every((v) => v.title.trim() && v.body.trim());

  const splitEvenly = () => {
    const base = Math.floor(100 / variants.length);
    const rest = 100 - base * variants.length;
    setVariants((prev) =>
      prev.map((v, i) => ({ ...v, traffic_percentage: base + (i < rest ? 1 : 0) }))
    );
  };

  const handlePercentageChange = (key: string, value: number) => {
    setVariants((prev) => {
      if (prev.length === 1) return prev;
      const others = prev.filter((v) => v.key !== key);
      const remaining = 100 - value;
      const otherTotal = others.reduce((s, v) => s + v.traffic_percentage, 0) || 1;

      let assigned = 0;
      const rebalanced = others.map((v, i) => {
        const share =
          i === others.length - 1
            ? remaining - assigned
            : Math.max(1, Math.round((v.traffic_percentage / otherTotal) * remaining));
        assigned += share;
        return { ...v, traffic_percentage: share };
      });

      return prev.map((v) =>
        v.key === key
          ? { ...v, traffic_percentage: value }
          : rebalanced.find((r) => r.key === v.key) || v
      );
    });
  };

  const loadAudience = useCallback(async () => {
    if (!selectedAppId) {
      setAudience(null);
      return;
    }
    setAudienceLoading(true);
    setAudienceError(undefined);
    try {
      const preview = await abTestApi.previewAudience({
        app_id: selectedAppId,
        segment_id: useSegment ? segmentId || undefined : undefined,
        locale: !useSegment ? locale || undefined : undefined,
        os: !useSegment ? os || undefined : undefined,
        offer: !useSegment ? offer || undefined : undefined,
        has_registration: !useSegment ? hasReg : undefined,
        has_deposit: !useSegment ? hasDep : undefined,
        variants: variants.map((v) => ({
          name: v.name,
          traffic_percentage: v.traffic_percentage,
        })),
      });
      setAudience(preview);
    } catch (e: any) {
      setAudience(null);
      setAudienceError(e?.response?.data?.error || 'Не удалось посчитать аудиторию');
    } finally {
      setAudienceLoading(false);
    }
  }, [selectedAppId, useSegment, segmentId, locale, os, offer, hasReg, hasDep, variants]);

  useEffect(() => {
    const t = window.setTimeout(loadAudience, 600);
    return () => window.clearTimeout(t);
  }, [loadAudience]);

  const requiredPerVariant = useMemo(() => {
    const p = 0.02;
    const mde = 0.1;
    const p2 = p * (1 + mde);
    const zA = 1.96;
    const zB = 0.84;
    const pBar = (p + p2) / 2;
    const num =
      zA * Math.sqrt(2 * pBar * (1 - pBar)) +
      zB * Math.sqrt(p * (1 - p) + p2 * (1 - p2));
    return Math.ceil((num * num) / Math.pow(p2 - p, 2));
  }, []);

  const audienceEnough =
    audience && audience.remaining > 0
      ? Math.min(...variants.map((v) => (audience.remaining * v.traffic_percentage) / 100)) >=
        requiredPerVariant
      : null;

  const openVariantModal = (variant?: VariantForm) => {
    setEditingVariant(variant || null);
    variantForm.resetFields();
    variantForm.setFieldsValue(
      variant || {
        is_control: false,
        traffic_percentage: Math.max(5, Math.floor(100 / (variants.length + 1))),
      }
    );
    setModalVisible(true);
  };

  const handleVariantModalOk = async () => {
    const values = await variantForm.validateFields();

    if (values.custom_data) {
      try {
        JSON.parse(values.custom_data);
      } catch {
        notify.error('custom_data должен быть валидным JSON');
        return;
      }
    }

    const data: VariantForm = {
      key: editingVariant?.key || newKey(),
      name: values.name.trim(),
      is_control: !!values.is_control,
      traffic_percentage: values.traffic_percentage,
      title: values.title,
      body: values.body,
      image_url: values.image_url,
      click_url: values.click_url,
      custom_data: values.custom_data,
    };

    setVariants((prev) => {
      const cleared = data.is_control ? prev.map((v) => ({ ...v, is_control: false })) : prev;
      return editingVariant
        ? cleared.map((v) => (v.key === editingVariant.key ? data : v))
        : [...cleared, data];
    });

    setModalVisible(false);
    variantForm.resetFields();
  };

  const duplicateVariant = (variant: VariantForm) => {
    setVariants((prev) => [
      ...prev,
      {
        ...variant,
        key: newKey(),
        name: `${variant.name} (копия)`,
        is_control: false,
        traffic_percentage: Math.max(1, Math.floor(variant.traffic_percentage / 2)),
      },
    ]);
  };

  const deleteVariant = (key: string) => {
    if (variants.length <= 2) {
      notify.error('В тесте должно остаться минимум 2 варианта');
      return;
    }
    setVariants((prev) => prev.filter((v) => v.key !== key));
  };
  
  const handleSubmit = async (values: any) => {
    if (!isPercentageValid) {
      notify.error(`Сумма процентов трафика — ${totalPercentage}%, должна быть 100%`);
      return;
    }
    if (!hasControl) {
      notify.error('Отметьте контрольный вариант');
      return;
    }
    const empty = variants.find((v) => !v.title.trim() || !v.body.trim());
    if (empty) {
      notify.error(`Заполните заголовок и текст варианта «${empty.name}»`);
      return;
    }

    const [startDate, endDate] = values.dateRange || [];
    if (!startDate) {
      notify.error('Укажите период проведения теста');
      return;
    }

    const payloadVariants: ABTestVariantCreateRequest[] = variants.map((v) => ({
      name: v.name,
      is_control: v.is_control,
      traffic_percentage: v.traffic_percentage,
      title: v.title,
      body: v.body,
      image_url: v.image_url || undefined,
      click_url: v.click_url || undefined,
      custom_data: v.custom_data ? JSON.parse(v.custom_data) : undefined,
    }));

    const payload: ABTestCreateRequest = {
      app_id: values.app_id,
      name: values.name,
      description: values.description,
      start_date: startDate.toISOString(),
      end_date: endDate ? endDate.toISOString() : undefined,
      variants: payloadVariants,
    };

    if (useSegment && values.segment_id) {
      payload.segment_id = values.segment_id;
    } else if (!useSegment) {
      if (values.locale) payload.locale = values.locale;
      if (values.os) payload.os = values.os;
      if (values.offer) payload.offer = values.offer;
      if (values.has_registration !== undefined)
        payload.has_registration = values.has_registration;
      if (values.has_deposit !== undefined) payload.has_deposit = values.has_deposit;
    }

    try {
      if (isEdit && editId) {
        await dispatch(updateABTest({ id: editId, data: payload })).unwrap();
        notify.success('Тест сохранён');
      } else {
        await dispatch(createABTest(payload)).unwrap();
        notify.success('A/B тест создан — запустите его, когда будете готовы');
      }
      navigate(ROUTES.PUSH_CENTER);
    } catch (e: any) {
      notify.error(typeof e === 'string' ? e : e?.message || 'Не удалось сохранить тест');
    }
  };

  const variantColumns: ColumnsType<VariantForm> = [
    {
      title: 'Вариант',
      dataIndex: 'name',
      width: 160,
      render: (name: string, record) => (
        <Space direction="vertical" size={2}>
          <Space size={6}>
            <Text strong>{name}</Text>
            {record.is_control && <Tag color="blue">Контроль</Tag>}
          </Space>
          {!record.title || !record.body ? (
            <Text type="danger" style={{ fontSize: 11 }}>
              не заполнен
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Контент',
      key: 'content',
      render: (_, record) => (
        <PushPreview title={record.title} body={record.body} imageUrl={record.image_url} />
      ),
    },
    {
      title: 'Трафик',
      dataIndex: 'traffic_percentage',
      width: 220,
      render: (percentage: number, record) => (
        <Space direction="vertical" style={{ width: '100%' }} size={0}>
          <Slider
            min={1}
            max={99}
            value={percentage}
            onChange={(val) => handlePercentageChange(record.key, val)}
            tooltip={{ formatter: (v) => `${v}%` }}
          />
          <Space>
            <Text strong>{percentage}%</Text>
            {audience && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                ≈ {Math.round((audience.remaining * percentage) / 100).toLocaleString('ru-RU')}{' '}
                чел.
              </Text>
            )}
          </Space>
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" onClick={() => openVariantModal(record)}>
            Изменить
          </Button>
          <Tooltip title="Дублировать">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => duplicateVariant(record)}
            />
          </Tooltip>
          <Tooltip title="Удалить">
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => deleteVariant(record.key)}
              disabled={variants.length <= 2}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  if (loadingTest) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="page-container">
      <Card>
        <div className="page-header">
          <Title level={2}>
            <ExperimentOutlined style={{ marginRight: 12 }} />
            {isEdit ? 'Редактирование A/B теста' : 'Создание A/B теста'}
          </Title>
          <Text type="secondary">
            Меняйте что-то одно за раз — иначе непонятно, что именно сработало
          </Text>
        </div>

        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={24}>
            <Col xs={24} lg={16}>
              <Card title="Основное" style={{ marginBottom: 16 }}>
                <Form.Item
                  name="name"
                  label="Название теста"
                  rules={[{ required: true, message: 'Введите название' }]}
                >
                  <Input placeholder="Например: CTA «Забрать бонус» против «Играть»" />
                </Form.Item>

                <Form.Item name="description" label="Гипотеза">
                  <TextArea
                    placeholder="Что проверяем и какого результата ждём"
                    rows={2}
                    maxLength={500}
                  />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="app_id"
                      label="Приложение"
                      rules={[{ required: true, message: 'Выберите приложение' }]}
                    >
                      <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder="Выберите приложение"
                        disabled={isEdit}
                        options={applications.map((app: any) => ({
                          value: app.id,
                          label: app.name,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="dateRange"
                      label="Период проведения"
                      rules={[{ required: true, message: 'Выберите период' }]}
                    >
                      <RangePicker
                        showTime
                        format="DD.MM.YYYY HH:mm"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              <Card title="Кому отправляем" style={{ marginBottom: 16 }}>
                <Form.Item>
                  <Space>
                    <Switch checked={useSegment} onChange={setUseSegment} />
                    <Text>Использовать готовый сегмент</Text>
                  </Space>
                </Form.Item>

                {useSegment ? (
                  <Form.Item
                    name="segment_id"
                    label="Сегмент"
                    extra="Без сегмента тест уйдёт на всех активных пользователей приложения"
                  >
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder="Все пользователи"
                      disabled={!selectedAppId}
                      options={segments.map((seg: any) => ({
                        value: seg.id,
                        label: `${seg.name} (${seg.user_count ?? 0})`,
                      }))}
                    />
                  </Form.Item>
                ) : (
                  <>
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item name="locale" label="Язык">
                          <Select allowClear placeholder="Любой">
                            {LOCALES.map((l) => (
                              <Select.Option key={l} value={l}>
                                {l.toUpperCase()}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="os" label="Платформа">
                          <Select allowClear placeholder="Любая">
                            {OS_OPTIONS.map((o) => (
                              <Select.Option key={o} value={o}>
                                {o.toUpperCase()}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item name="offer" label="Оффер">
                          <Input allowClear placeholder="Любой" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="has_registration" label="Регистрация">
                          <Select allowClear placeholder="Неважно">
                            <Select.Option value={true}>Есть</Select.Option>
                            <Select.Option value={false}>Нет</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="has_deposit" label="Депозит">
                          <Select allowClear placeholder="Неважно">
                            <Select.Option value={true}>Есть</Select.Option>
                            <Select.Option value={false}>Нет</Select.Option>
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}
              </Card>

              <Card
                title={
                  <Space>
                    <span>Варианты</span>
                    <Tag color={isPercentageValid ? 'success' : 'error'}>
                      {totalPercentage}% / 100%
                    </Tag>
                  </Space>
                }
                extra={
                  <Space>
                    <Button icon={<ThunderboltOutlined />} onClick={splitEvenly}>
                      Поровну
                    </Button>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => openVariantModal()}
                    >
                      Добавить
                    </Button>
                  </Space>
                }
              >
                {!hasControl && (
                  <Alert
                    type="error"
                    showIcon
                    message="Отметьте контрольный вариант — с ним будут сравниваться остальные"
                    style={{ marginBottom: 16 }}
                  />
                )}

                <Table
                  columns={variantColumns}
                  dataSource={variants}
                  pagination={false}
                  rowKey="key"
                  size="small"
                />
              </Card>
            </Col>

            <Col xs={24} lg={8}>
              <Card
                title={
                  <Space>
                    <TeamOutlined />
                    Аудитория
                  </Space>
                }
                style={{ marginBottom: 16 }}
              >
                {audienceLoading ? (
                  <Spin />
                ) : audienceError ? (
                  <Alert type="warning" showIcon message={audienceError} />
                ) : audience ? (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Statistic
                      title="Подходит под условия"
                      value={audience.total}
                      groupSeparator=" "
                    />
                    {audience.already_sent > 0 && (
                      <Text type="secondary">
                        уже получали пуш по этому тесту:{' '}
                        {audience.already_sent.toLocaleString('ru-RU')} → в рассылку пойдёт{' '}
                        {audience.remaining.toLocaleString('ru-RU')}
                      </Text>
                    )}
                    <Divider style={{ margin: '8px 0' }} />
                    {variants.map((v) => (
                      <Row key={v.key} justify="space-between">
                        <Text>{v.name}</Text>
                        <Text strong>
                          ≈{' '}
                          {Math.round(
                            (audience.remaining * v.traffic_percentage) / 100
                          ).toLocaleString('ru-RU')}
                        </Text>
                      </Row>
                    ))}

                    {audienceEnough === false && (
                      <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 8 }}
                        message="Выборки может не хватить"
                        description={`Чтобы поймать разницу в 10% при CTR ~2%, нужно около ${requiredPerVariant.toLocaleString(
                          'ru-RU'
                        )} показов на вариант. Возьмите более широкий сегмент или сократите число вариантов.`}
                      />
                    )}
                    {audienceEnough === true && (
                      <Alert
                        type="success"
                        showIcon
                        style={{ marginTop: 8 }}
                        message="Выборки достаточно для значимого вывода"
                      />
                    )}
                  </Space>
                ) : (
                  <Text type="secondary">Выберите приложение, чтобы увидеть охват</Text>
                )}
              </Card>

              <Card title="Действия">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Alert
                    type="info"
                    icon={<InfoCircleOutlined />}
                    showIcon
                    message="Тест создаётся черновиком. Запустите его вручную, а затем отправьте пуши из списка тестов."
                  />
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={isSaving}
                    size="large"
                    block
                    disabled={!isPercentageValid || !hasControl || !allFilled}
                  >
                    {isEdit ? 'Сохранить' : 'Создать A/B тест'}
                  </Button>
                  <Button onClick={() => navigate(ROUTES.PUSH_CENTER)} block>
                    Отмена
                  </Button>
                </Space>

                <Divider />

                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  <Text strong>Как получить внятный результат</Text>
                  <ul style={{ paddingLeft: 18, marginTop: 8 }}>
                    <li>Меняйте что-то одно: заголовок ИЛИ картинку, не всё сразу</li>
                    <li>Контроль — ваш текущий рабочий вариант</li>
                    <li>Не останавливайте тест на первом же перевесе</li>
                    <li>Два варианта дают вывод быстрее, чем пять</li>
                  </ul>
                </Paragraph>
              </Card>
            </Col>
          </Row>
        </Form>
      </Card>

      <Modal
        title={editingVariant ? `Вариант «${editingVariant.name}»` : 'Новый вариант'}
        open={modalVisible}
        onOk={handleVariantModalOk}
        onCancel={() => setModalVisible(false)}
        width={640}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={variantForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Название варианта"
                rules={[{ required: true, message: 'Введите название' }]}
              >
                <Input placeholder="Variant B" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="traffic_percentage"
                label="Процент трафика"
                rules={[{ required: true, message: 'Укажите процент' }]}
              >
                <InputNumber min={1} max={99} style={{ width: '100%' }} addonAfter="%" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="is_control" valuePropName="checked" label="Контрольная группа">
            <Switch />
          </Form.Item>

          <Divider>Содержимое пуша</Divider>

          <Form.Item
            name="title"
            label="Заголовок"
            rules={[{ required: true, message: 'Введите заголовок' }]}
          >
            <Input placeholder="Заголовок уведомления" maxLength={100} showCount />
          </Form.Item>

          <Form.Item
            name="body"
            label="Текст"
            rules={[{ required: true, message: 'Введите текст' }]}
          >
            <TextArea rows={3} maxLength={500} showCount placeholder="Текст уведомления" />
          </Form.Item>

          <Form.Item name="image_url" label="URL изображения">
            <Input placeholder="https://example.com/image.jpg" />
          </Form.Item>

          <Form.Item
            name="click_url"
            label="Ссылка по клику"
            extra="Куда откроется приложение или PWA после тапа по уведомлению"
            rules={[{ type: 'url', warningOnly: true, message: 'Похоже, это не URL' }]}
          >
            <Input placeholder="https://example.com/promo" />
          </Form.Item>

          <Form.Item
            name="custom_data"
            label="custom_data (JSON)"
            extra="Уйдёт в payload уведомления. Необязательно."
          >
            <TextArea rows={3} placeholder='{"promo": "welcome_100"}' />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ABTestForm;